import * as CryptoJS from 'crypto-js';

const CHUNK_BYTES = 1024 * 1024;
// Native hashing reads the whole file into memory; above this the incremental hash is used instead
const NATIVE_MAX_BYTES = 512 * 1024 * 1024;

/**
 * The file's SHA-256 as 64 lowercase hex characters. On HTTPS the browser's native SubtleCrypto hashes a 132 MB set in
 * well under a second; incremental CryptoJS (about 30 s for that set) is the fallback on local HTTP domains, where
 * SubtleCrypto is unavailable, and for very large files, since it reads 1 MB at a time.
 */
export async function sha256File(
  file: Blob,
  opts: { signal?: AbortSignal; onProgress?: (loaded: number, total: number) => void } = {}
): Promise<string> {
  const { signal, onProgress } = opts;
  const subtle = globalThis.crypto?.subtle;
  if (subtle && file.size <= NATIVE_MAX_BYTES) {
    signal?.throwIfAborted();
    const digest = await subtle.digest('SHA-256', await file.arrayBuffer());
    signal?.throwIfAborted();
    onProgress?.(file.size, file.size);
    return Array.from(new Uint8Array(digest), byte => byte.toString(16).padStart(2, '0')).join('');
  }
  const hash = CryptoJS.algo.SHA256.create();
  for (let offset = 0; offset < file.size; offset += CHUNK_BYTES) {
    signal?.throwIfAborted();
    const buffer = await file.slice(offset, offset + CHUNK_BYTES).arrayBuffer();
    signal?.throwIfAborted();
    hash.update(CryptoJS.lib.WordArray.create(buffer));
    onProgress?.(Math.min(offset + CHUNK_BYTES, file.size), file.size);
  }
  return hash.finalize().toString(CryptoJS.enc.Hex);
}
